/**
 * Clipboard Plugin for Lexical
 * Handles paste events with HTML to AST conversion
 * Properly converts AST content to Lexical nodes with formatting
 */

import { $generateNodesFromSerializedNodes, $insertGeneratedNodes } from '@lexical/clipboard'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import {
  $createTextNode,
  $getRoot,
  $getSelection,
  $insertNodes,
  $isRangeSelection,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  COPY_COMMAND,
  CUT_COMMAND,
  LexicalNode,
  PASTE_COMMAND,
  TextNode,
} from 'lexical'
import { useEffect } from 'react'
import toast from 'react-hot-toast'

import {
  Block,
  HeadingBlock,
  InlineContent,
  ListBlock,
  ListItem,
  Mark,
  ParagraphBlock,
  QuoteBlock,
  generateBlockId,
  importHtmlToAst,
  importTextToAst,
} from '../../../../transforms/ast'
import { logger } from '../../../../utils/logger'
import { EMBED_BOOK_CLIPBOARD_TYPE, canInsertInlineEmbed } from '../embedClipboard'
import {
  $createCustomEmbedNode,
  $createCustomHeadingNode,
  $createCustomInlineEmbedNode,
  $createCustomLinkNode,
  $createCustomListItemNode,
  $createCustomListNode,
  $createCustomParagraphNode,
  $createCustomQuoteNode,
  HeadingLevel,
} from '../nodes'

export interface ClipboardPluginProps {
  defaultLang?: 'he' | 'en' | 'mixed'
  defaultDir?: 'rtl' | 'ltr'
  bookId?: string
}

/**
 * Converts AST marks to Lexical text format
 * Returns a bitmask for text formatting
 */
function marksToFormat(marks?: Mark[]): number {
  if (!marks || marks.length === 0) {
    return 0
  }

  let format = 0
  for (const mark of marks) {
    if (mark === 'bold') {
      format |= 1
    } // IS_BOLD
    if (mark === 'italic') {
      format |= 2
    } // IS_ITALIC
    if (mark === 'underline') {
      format |= 8
    } // IS_UNDERLINE
  }
  return format
}

/**
 * Creates a Lexical TextNode with proper formatting from AST marks
 */
function createFormattedTextNode(text: string, marks?: Mark[]): TextNode {
  const textNode = $createTextNode(text)
  const format = marksToFormat(marks)
  if (format > 0) {
    textNode.setFormat(format)
  }
  return textNode
}

/**
 * Converts AST inline content to Lexical nodes
 */
function convertInlineContent(content: InlineContent[]): LexicalNode[] {
  const nodes: LexicalNode[] = []

  for (const item of content) {
    if (item.type === 'text') {
      nodes.push(createFormattedTextNode(item.text, item.marks))
    } else if (item.type === 'inline-embed') {
      nodes.push($createCustomInlineEmbedNode(item.source))
    } else if (item.type === 'link') {
      // Create link node with text content
      const linkNode = $createCustomLinkNode(item.url)
      // Add formatted text inside the link
      for (const textNode of item.content) {
        linkNode.append(createFormattedTextNode(textNode.text, textNode.marks))
      }
      nodes.push(linkNode)
    } else if (item.type === 'encrypted') {
      // For encrypted content, just show the text content for now
      for (const textNode of item.content) {
        nodes.push(createFormattedTextNode(textNode.text, textNode.marks))
      }
    }
  }

  return nodes
}

/**
 * Converts AST paragraph block to Lexical paragraph node
 */
function convertParagraph(block: ParagraphBlock): LexicalNode {
  const paragraph = $createCustomParagraphNode(block.id || generateBlockId())
  if (block.attrs?.dir) {
    paragraph.setDir(block.attrs.dir)
  }
  const inlineNodes = convertInlineContent(block.content)
  paragraph.append(...inlineNodes)
  return paragraph
}

/**
 * Converts AST heading block to Lexical heading node
 */
function convertHeading(block: HeadingBlock): LexicalNode {
  const heading = $createCustomHeadingNode(block.id || generateBlockId(), block.level as HeadingLevel)
  if (block.attrs?.dir) {
    heading.setDir(block.attrs.dir)
  }
  const inlineNodes = convertInlineContent(block.content)
  heading.append(...inlineNodes)
  return heading
}

/**
 * Converts AST quote block to Lexical quote node
 */
function convertQuote(block: QuoteBlock): LexicalNode {
  const quote = $createCustomQuoteNode(block.id || generateBlockId())
  if (block.attrs?.dir) {
    quote.setDir(block.attrs.dir)
  }
  const inlineNodes = convertInlineContent(block.content)
  quote.append(...inlineNodes)
  return quote
}

/**
 * Recursively converts AST list items to Lexical list item nodes
 */
function convertListItems(items: ListItem[], ordered: boolean, depth: number = 0): LexicalNode[] {
  const nodes: LexicalNode[] = []

  for (const item of items) {
    const listItem = $createCustomListItemNode(item.id || generateBlockId())
    if (item.attrs?.dir) {
      listItem.setDir(item.attrs.dir)
    }

    // Add inline content to the list item
    const inlineNodes = convertInlineContent(item.content)
    listItem.append(...inlineNodes)

    nodes.push(listItem)

    // Handle nested children - create a nested list
    if (item.children && item.children.length > 0) {
      // Create a nested list for children
      const nestedList = $createCustomListNode(generateBlockId(), ordered)
      const childItems = convertListItems(item.children, ordered, depth + 1)
      nestedList.append(...childItems)

      // Create a wrapper list item for the nested list (Lexical's nested list structure)
      const wrapperItem = $createCustomListItemNode(generateBlockId())
      wrapperItem.append(nestedList)
      nodes.push(wrapperItem)
    }
  }

  return nodes
}

/**
 * Converts AST list block to Lexical list node
 */
function convertList(block: ListBlock): LexicalNode {
  const list = $createCustomListNode(block.id || generateBlockId(), block.ordered)
  if (block.attrs?.dir) {
    list.setDir(block.attrs.dir)
  }

  const listItems = convertListItems(block.items, block.ordered)
  list.append(...listItems)

  return list
}

/**
 * Converts an AST block to a Lexical node
 */
function convertBlock(block: Block): LexicalNode | null {
  switch (block.type) {
    case 'paragraph':
      return convertParagraph(block)
    case 'heading':
      return convertHeading(block)
    case 'quote':
      return convertQuote(block)
    case 'list':
      return convertList(block)
    case 'divider':
      // For divider, create an empty paragraph (or could use a divider node)
      return $createCustomParagraphNode(block.id || generateBlockId())
    case 'embed':
      return $createCustomEmbedNode(block.id || generateBlockId(), block.source)
    case 'image':
      // For images, create a paragraph placeholder
      return $createCustomParagraphNode(block.id || generateBlockId())
    default:
      return null
  }
}

// Custom node types whose serialized payload carries a `blockId`. These IDs
// must stay unique within a source file — chapterSectionKey refs, annotations,
// publishedSections arrays, and findIndex-based lookups all depend on it.
const CUSTOM_NODE_TYPES_WITH_BLOCK_ID = new Set([
  'custom-paragraph',
  'custom-heading',
  'custom-quote',
  'custom-divider',
  'custom-image',
  'custom-embed',
  'custom-list',
  'custom-listitem',
])

function hasCustomBlockId(value: unknown): value is { type: string; blockId: string; children?: unknown } {
  if (!value || typeof value !== 'object') {
    return false
  }
  const n = value as { type?: unknown; blockId?: unknown }
  return typeof n.type === 'string' && CUSTOM_NODE_TYPES_WITH_BLOCK_ID.has(n.type) && typeof n.blockId === 'string'
}

/** Collects every blockId currently present in the editor's node tree. */
function collectExistingBlockIds(): Set<string> {
  const ids = new Set<string>()
  const walk = (node: LexicalNode): void => {
    const anyNode = node as LexicalNode & { getBlockId?: () => string; getChildren?: () => LexicalNode[] }
    if (typeof anyNode.getBlockId === 'function') {
      const id = anyNode.getBlockId()
      if (typeof id === 'string' && id) {
        ids.add(id)
      }
    }
    if (typeof anyNode.getChildren === 'function') {
      for (const child of anyNode.getChildren()) {
        walk(child)
      }
    }
  }
  for (const child of $getRoot().getChildren()) {
    walk(child)
  }
  return ids
}

/**
 * Rewrite blockIds in the serialized paste payload only where they would
 * collide with an existing ID. Cut-paste leaves the source blockId absent
 * (it was just removed), so the paste reinstates it verbatim — annotations,
 * chapterSection refs, and publishedSections stay attached. Copy-paste
 * leaves the source blockId present, so the duplicate gets a fresh ID.
 *
 * Returns whether any IDs were regenerated (for logging).
 */
function regenerateConflictingBlockIds(nodes: unknown[], usedIds: Set<string>): number {
  let regenerated = 0
  const walk = (list: unknown[]): void => {
    for (const node of list) {
      if (hasCustomBlockId(node)) {
        if (usedIds.has(node.blockId)) {
          let fresh = generateBlockId()
          while (usedIds.has(fresh)) {
            fresh = generateBlockId()
          }
          node.blockId = fresh
          regenerated++
        }
        usedIds.add(node.blockId)
      }
      const children = (node as { children?: unknown })?.children
      if (Array.isArray(children)) {
        walk(children)
      }
    }
  }
  walk(nodes)
  return regenerated
}

export function ClipboardPlugin({ defaultLang = 'mixed', defaultDir = 'ltr', bookId }: ClipboardPluginProps) {
  const [editor] = useLexicalComposerContext()

  useEffect(() => {
    const markOrigin = (event: ClipboardEvent | KeyboardEvent | null): boolean => {
      if (bookId && event instanceof ClipboardEvent) {
        event.clipboardData?.setData(EMBED_BOOK_CLIPBOARD_TYPE, bookId)
      }
      return false
    }
    const copy = editor.registerCommand(COPY_COMMAND, markOrigin, COMMAND_PRIORITY_HIGH)
    const cut = editor.registerCommand(CUT_COMMAND, markOrigin, COMMAND_PRIORITY_HIGH)
    return () => {
      copy()
      cut()
    }
  }, [bookId, editor])

  useEffect(() => {
    return editor.registerCommand(
      PASTE_COMMAND,
      (event: ClipboardEvent) => {
        const clipboardData = event.clipboardData
        if (!clipboardData) {
          return false
        }

        const htmlData = clipboardData.getData('text/html')
        const textData = clipboardData.getData('text/plain')

        // Internal Lexical paste (same-tab copy-paste / cut-paste). Lexical's
        // native handler calls importJSON on each custom node and preserves
        // `blockId` verbatim — which is correct for cut-paste (the move should
        // keep annotations, chapter refs, publishedSections anchored) but wrong
        // for copy-paste (N copies would share one ID and break findIndex
        // lookups). The two operations look identical at paste time, so we
        // distinguish by collision: IDs already present in the document came
        // from a copy and must be regenerated; absent IDs came from a cut and
        // are safe to reinstate.
        const internalPayload = clipboardData.getData('application/x-lexical-editor')
        if (internalPayload) {
          try {
            const parsed = JSON.parse(internalPayload)
            const serializedNodes = Array.isArray(parsed?.nodes) ? parsed.nodes : null
            if (serializedNodes) {
              const hasEmbed = (nodes: unknown[], inlineOnly = false): boolean =>
                nodes.some((node) => {
                  if (!node || typeof node !== 'object') {
                    return false
                  }
                  const value = node as { type?: string; children?: unknown[] }
                  return (
                    (!inlineOnly && value.type === 'custom-embed') ||
                    value.type === 'custom-inline-embed' ||
                    Boolean(Array.isArray(value.children) && hasEmbed(value.children, inlineOnly))
                  )
                })
              if (
                hasEmbed(serializedNodes) &&
                (!bookId || clipboardData.getData(EMBED_BOOK_CLIPBOARD_TYPE) !== bookId)
              ) {
                event.preventDefault()
                toast.error('אפשר להעתיק שיבוץ בתוך אותו ספר בלבד; העתיקו שוב מהמקור')
                return true
              }
              if (hasEmbed(serializedNodes, true)) {
                const selection = $getSelection()
                if ($isRangeSelection(selection) && !canInsertInlineEmbed(selection)) {
                  event.preventDefault()
                  toast.error('יש להציב את הסמן בתוך פסקה, מחוץ לקישור או לטקסט מוצפן')
                  return true
                }
              }
              let regenerated = 0
              editor.getEditorState().read(() => {
                const existing = collectExistingBlockIds()
                regenerated = regenerateConflictingBlockIds(serializedNodes, existing)
              })

              if (regenerated === 0) {
                // Cut-paste (or external internal paste with no collisions):
                // let Lexical's native handler run so IDs are preserved.
                return false
              }

              event.preventDefault()
              editor.update(() => {
                const selection = $getSelection()
                if (!$isRangeSelection(selection)) {
                  return
                }
                const nodes = $generateNodesFromSerializedNodes(serializedNodes)
                $insertGeneratedNodes(editor, nodes, selection)
              })
              logger.debug('Internal paste: regenerated colliding blockIds', { count: regenerated })
              return true
            }
          } catch (err) {
            logger.warn('Internal paste: failed to inspect payload, falling back to native handler', {
              error: (err as Error).message,
            })
          }
          return false
        }

        // If we have HTML content, process it through our AST pipeline
        if (htmlData && htmlData.trim()) {
          event.preventDefault()

          try {
            const result = importHtmlToAst(htmlData, {
              defaultLang,
              defaultDir,
            })

            if (result.success && result.ast) {
              logger.debug('Clipboard: Parsed HTML to AST:', result.ast.blocks.length, 'blocks')

              // Insert the content
              editor.update(() => {
                const selection = $getSelection()
                if (!$isRangeSelection(selection)) {
                  return
                }

                // For simple single-line text without formatting, just insert as text
                if (result.ast!.blocks.length === 1) {
                  const block = result.ast!.blocks[0]
                  if (block.type === 'paragraph' && 'content' in block) {
                    // Check if it's simple text (single text node, no marks, no newlines)
                    if (block.content.length === 1 && block.content[0].type === 'text') {
                      const textNode = block.content[0]
                      if (!textNode.marks?.length && !textNode.text.includes('\n')) {
                        const node = $createTextNode(textNode.text)
                        selection.insertNodes([node])
                        return
                      }
                    }
                  }
                }

                // Convert AST blocks to Lexical nodes with proper formatting
                const nodes: LexicalNode[] = []
                for (const block of result.ast!.blocks) {
                  const node = convertBlock(block)
                  if (node) {
                    nodes.push(node)
                  }
                }

                if (nodes.length > 0) {
                  $insertNodes(nodes)
                }
              })

              return true
            }
          } catch (error) {
            logger.error('Clipboard: Failed to parse HTML:', error)
          }
        }

        // Fall back to plain text
        if (textData && textData.trim()) {
          event.preventDefault()

          try {
            const result = importTextToAst(textData, {
              defaultLang,
              defaultDir,
            })

            if (result.success && result.ast) {
              editor.update(() => {
                const selection = $getSelection()
                if (!$isRangeSelection(selection)) {
                  return
                }

                // For single-line text, insert directly
                if (!textData.includes('\n')) {
                  const textNode = $createTextNode(textData)
                  selection.insertNodes([textNode])
                  return
                }

                // For multi-line text, create paragraphs
                const lines = textData.split('\n')
                const nodes = lines.map((line) => {
                  const paragraph = $createCustomParagraphNode(generateBlockId())
                  paragraph.append($createTextNode(line))
                  return paragraph
                })

                $insertNodes(nodes)
              })

              return true
            }
          } catch (error) {
            logger.error('Clipboard: Failed to parse text:', error)
          }
        }

        return false
      },
      COMMAND_PRIORITY_LOW,
    )
  }, [editor, defaultLang, defaultDir, bookId])

  return null
}

export default ClipboardPlugin
